import { useCallback, useEffect, useState } from 'react';

import { Box as Flex, Tooltip } from '@material-ui/core';

import {
  RiDeleteBinLine,
  RiDeleteColumn,
  RiDeleteRow,
  RiInsertColumnLeft,
  RiInsertColumnRight,
  RiInsertRowBottom,
  RiInsertRowTop,
} from '@remixicon/react';
import type { ResolvedPos } from '@tiptap/pm/model';
import { BubbleMenu } from '@tiptap/react/menus';

import { CommentZoneProvider } from '@spotify/plugin-confidence-comments-react';

import { useBlockZoneId } from '../../hooks';
import { generateBlockId } from '../../utils';
import { BlockReorderButtons } from '../BlockReorderButtons';
import { BlockCommentButton } from '../Comment';
import { useCurrentEditor } from '../Context';
import { ToolbarCard } from '../FloatingCard';
import { ToolbarButton } from '../ToolbarButton';

const findTableInfo = (state: { selection: { $from: ResolvedPos } }) => {
  const { $from } = state.selection;
  let depth = $from.depth;
  while (depth > 0) {
    if ($from.node(depth).type.name === 'table') {
      return { pos: $from.before(depth), node: $from.node(depth) };
    }
    depth--;
  }
  return null;
};

const TableToolbarContent = () => {
  const { editor } = useCurrentEditor();
  const [tableBlockId, setTableBlockId] = useState<string | null>(null);

  // Assign a blockId if the table doesn't have one yet
  useEffect(() => {
    if (!editor) return undefined;

    const recomputeTableBlockId = () => {
      const info = findTableInfo(editor.state);
      if (!info) {
        setTableBlockId(null);
        return;
      }

      const { pos, node } = info;

      if (!node.attrs.blockId) {
        const blockId = generateBlockId();
        const { tr } = editor.state;
        tr.setNodeAttribute(pos, 'blockId', blockId);
        editor.view.dispatch(tr);
        setTableBlockId(blockId);
        return;
      }

      setTableBlockId(node.attrs.blockId as string);
    };

    recomputeTableBlockId();
    editor.on('selectionUpdate', recomputeTableBlockId);
    editor.on('transaction', recomputeTableBlockId);

    return () => {
      editor.off('selectionUpdate', recomputeTableBlockId);
      editor.off('transaction', recomputeTableBlockId);
    };
  }, [editor]);

  const zoneId = useBlockZoneId('table', tableBlockId);

  const handleAddColumnBefore = useCallback(() => {
    editor?.chain().focus().addColumnBefore().run();
  }, [editor]);

  const handleAddColumnAfter = useCallback(() => {
    editor?.chain().focus().addColumnAfter().run();
  }, [editor]);

  const handleDeleteColumn = useCallback(() => {
    editor?.chain().focus().deleteColumn().run();
  }, [editor]);

  const handleAddRowBefore = useCallback(() => {
    editor?.chain().focus().addRowBefore().run();
  }, [editor]);

  const handleAddRowAfter = useCallback(() => {
    editor?.chain().focus().addRowAfter().run();
  }, [editor]);

  const handleDeleteRow = useCallback(() => {
    editor?.chain().focus().deleteRow().run();
  }, [editor]);

  const handleDeleteTable = useCallback(() => {
    if (!editor) return;
    const info = findTableInfo(editor.state);
    if (!info) return;
    const { pos, node } = info;
    editor
      .chain()
      .focus()
      .deleteRange({ from: pos, to: pos + node.nodeSize })
      .run();
  }, [editor]);

  const toolbar = (
    <ToolbarCard>
      <Flex
        display="flex"
        alignItems="center"
        style={{
          gap: 'var(--bui-space-0_5)',
        }}
      >
        <BlockReorderButtons />
        <Tooltip title="Insert column left">
          <ToolbarButton
            aria-label="Insert column left"
            onClick={handleAddColumnBefore}
          >
            <RiInsertColumnLeft />
          </ToolbarButton>
        </Tooltip>
        <Tooltip title="Insert column right">
          <ToolbarButton
            aria-label="Insert column right"
            onClick={handleAddColumnAfter}
          >
            <RiInsertColumnRight />
          </ToolbarButton>
        </Tooltip>
        <Tooltip title="Delete column">
          <ToolbarButton
            aria-label="Delete column"
            onClick={handleDeleteColumn}
          >
            <RiDeleteColumn />
          </ToolbarButton>
        </Tooltip>
        <Tooltip title="Insert row above">
          <ToolbarButton
            aria-label="Insert row above"
            onClick={handleAddRowBefore}
          >
            <RiInsertRowTop />
          </ToolbarButton>
        </Tooltip>
        <Tooltip title="Insert row below">
          <ToolbarButton
            aria-label="Insert row below"
            onClick={handleAddRowAfter}
          >
            <RiInsertRowBottom />
          </ToolbarButton>
        </Tooltip>
        <Tooltip title="Delete row">
          <ToolbarButton aria-label="Delete row" onClick={handleDeleteRow}>
            <RiDeleteRow />
          </ToolbarButton>
        </Tooltip>
        {zoneId && <BlockCommentButton />}
        <Tooltip title="Delete table">
          <ToolbarButton aria-label="Delete table" onClick={handleDeleteTable}>
            <RiDeleteBinLine />
          </ToolbarButton>
        </Tooltip>
      </Flex>
    </ToolbarCard>
  );

  return zoneId ? (
    <CommentZoneProvider zone={zoneId}>{toolbar}</CommentZoneProvider>
  ) : (
    toolbar
  );
};

export const TableToolbar = () => {
  const { editor } = useCurrentEditor();

  const getReferencedVirtualElement = useCallback(() => {
    if (!editor) return null;
    const info = findTableInfo(editor.state);
    if (!info) return null;
    const tableDOM = editor.view.nodeDOM(info.pos) as HTMLElement;
    if (!tableDOM) return null;
    return {
      getBoundingClientRect: () => tableDOM.getBoundingClientRect(),
    };
  }, [editor]);

  return (
    <BubbleMenu
      editor={editor ?? undefined}
      pluginKey="tableToolbar"
      getReferencedVirtualElement={getReferencedVirtualElement}
      options={{ offset: 6, placement: 'top-end' }}
      style={{ zIndex: 99999 }}
      appendTo={() => document.body}
      shouldShow={({ view, state }) => {
        if (!view.hasFocus()) return false;
        return findTableInfo(state) !== null;
      }}
    >
      <TableToolbarContent />
    </BubbleMenu>
  );
};
